<script module>
  let count = 0;
</script>

<script>
  import BaseSpinner from '../BaseSpinner/BaseSpinner.svelte';
  import handleFetch from '../../utils/handleFetch.ts';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import handleSAASError from '../../utils/handleSAASError';
  import { KEYCODES } from '../../utils/constants';
  import insideScoopImage from '../../assets/insidescoop.svg';
  
  let {
    theme = 'gray',
    headingText,
    optionalText = undefined,
    instructions,
    label,
    placeholder,
    submitButtonLabel,
    validationErrorText,
    errorText,
    tryAgainButtonLabel,
    successText,
    locale,
    small = false,
    fullWidth = false,
    incomingState = 'initial',
    lang = locale || 'en',
    dataTestId = 'newsletter-signup',
    newsletterSubmitted = $bindable(false),
    textCardOverpanel = false
  } = $props();

  const key = count++;

  let inputValue = $state('');
  let inputNode = $state();

  // Component Statement Managment
  const setState = (state) => {
    currentState = { ...initialStates, [state]: true };
  };

  let currentState = $state();

  const initialStates = {
    initial: false,
    populated: false,
    invalid: false,
    pending: false,
    failed: false,
    success: false,
  };

  setState(incomingState);

  let state = $derived(currentState);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setState('pending');
      const pending = await handleFetch(`${process.env.SAAS_NEWSLETTER_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': `${process.env.SAAS_API_KEY}`,
        },
        body: JSON.stringify({
          email: inputValue,
          locale: lang === 'es' ? 'es-US' : 'en-US',
        }),
      });
      const response = await pending.json();

      if (response.registered) {
        setState('success');
        newsletterSubmitted = true;
      } else {
        handleSAASError('Newsletter signup failed.', response.id, { errorMessage: response.message });
        setState('failed');
        newsletterSubmitted = true;
      }
    } catch {
      setState('failed');
    }

    window.umami.track(`newsletter-signup-${state.success ? 'success' : 'failed'}`);
  };

  const checkValidity = () => {
    if (!inputNode.validity.valid || !inputValue) {
      setState('invalid');
    }
  };

  const handleBlur = () => {
    if (!inputNode.validity.valid) {
      if (!inputValue) {
        reset();
      } else {
        setState('invalid');
      }
    }
  };

  const handleFocus = () => {
    setState('populated');
  };

  const handleRetry = () => {
    setState('populated');
  };

  const handleOnKeydown = (e) => {
    if (e.keyCode === KEYCODES.ESCAPE || e.keyCode === KEYCODES.ENTER) return;
    if (state.invalid) setState('populated');
  };

  const reset = () => {
    inputValue = '';
    setState('initial');
  };
</script>

<div
  data-testid={dataTestId}
  class="w-full print:hidden"
  class:h-full={!small}
  class:xl:mr-4={!textCardOverpanel}
  class:bg-gray-iron={theme === 'gray'}
  class:bg-blue-cerulean={theme === 'blue'}
  class:bg-gray-mercury={theme === 'white'}
  class:bg-gray-pale={theme === 'branded' && !fullWidth}
  class:h-auto={theme === 'branded' && small}
  class:bg-blue-very-light={theme === 'branded' && fullWidth}
>
  <div
    class="p-4 pb-0 mx-auto relative"
    class:max-w-120={!fullWidth}
    class:lg:px-6={theme === 'branded' && small}
    class:lg:px-8={theme === 'branded' && !small}
    class:lg:pt-8={theme === 'branded' && !small}
    class:md:flex={theme === 'branded' && fullWidth}
    class:md:max-w-168={theme === 'branded' && fullWidth}
    class:lg:max-w-200={theme === 'branded' && fullWidth}
  >
    {#if theme === 'branded'}
      <img
        class="max-w-24 absolute -top-9 right-4 lg:max-w-none"
        class:sm:max-w-20={small}
        class:sm:-top-6={small}
        class:lg:-top-12={small}
        class:lg:max-w-20={small}
        class:sm:right-0={!fullWidth}
        class:md:static={fullWidth}
        class:md:mr-4={fullWidth}
        class:md:w-44={fullWidth}
        class:md:max-w-44={fullWidth}
        class:md:mb-6={fullWidth}
        class:lg:mr-8={fullWidth}
        class:lg:max-w-52={fullWidth}
        class:lg:w-52={fullWidth}
        src={insideScoopImage}
        alt="Inside Scoop"
        height="106"
        width="106"
        loading={fullWidth ? 'lazy' : 'eager'}
      />
    {/if}
    <div>
      {#if state.success}
        <div class="flex justify-center items-center my-4">
          <svg
            class="w-16 h-16 fill-current"
            class:text-blue-cerulean={theme === 'gray'}
            class:text-blue-light={theme === 'blue'}
          >
            <use xlink:href={`${SPRITESHEET_URL}#check`} />
          </svg>
        </div>
        <p
          class="text-base leading-tight font-medium pb-12 px-8"
          class:text-blue-navy={theme === 'gray'}
          class:text-white={theme === 'blue'}
        >
          {successText}
        </p>
      {:else if state.failed}
        <div class="flex justify-center items-center mb-0">
          <svg
            class="w-16 h-16 fill-current text-red"
            class:text-red={theme === 'gray' || theme === 'branded'}
            class:text-white={theme === 'blue'}
          >
            <use xlink:href={`${SPRITESHEET_URL}#alert`} />
          </svg>
        </div>
        <p
          class="text-base leading-tight font-medium my-4 px-8"
          class:text-blue-navy={theme === 'gray'}
          class:text-white={theme === 'blue'}
        >
          {errorText}
        </p>
        <div class="flex justify-center items-center mb-8">
          <button
            class="leading-loose text-center font-medium bg-blue-robin-egg hover-hover:hover:bg-blue-light active:bg-blue-malibu
            focus:outline-none focus:shadow-focus active:shadow-active rounded-md whitespace-nowrap min-w-25 px-4 flex
            items-center justify-center h-12 mt-0.5 mb-8"
            data-testid="{dataTestId}-try-again-button"
            onclick={handleRetry}
          >
            {tryAgainButtonLabel}
          </button>
        </div>
      {:else}
        <div
          class:hidden={textCardOverpanel}
          class="w-full min-h-12"
          class:flex={headingText}
          class:items-center={headingText}
          class:justify-between={theme === 'branded'}
          class:mb-2={!small}
        >
          {#if theme !== 'branded'}
            <svg
              class="w-12 h-12 fill-current -mt-4 mr-4"
              class:text-blue-cerulean={theme === 'gray' || theme === 'white'}
              class:text-blue-light={theme === 'blue'}
            >
              <use xlink:href={`${SPRITESHEET_URL}#email`} />
            </svg>
          {/if}
          {#if headingText}
            <h2
              class="font-medium leading-snug my-2"
              class:text-lg={theme !== 'branded' || (theme === 'branded' && small)}
              class:text-blue-navy={theme === 'branded'}
              class:text-xl={theme === 'branded' && !small}
              class:md:text-1.5xl={theme === 'branded' && !small}
              class:lg:text-2xl={theme === 'branded' && !small}
              class:xl:text-xl={theme === 'branded' && small}
            >
              {headingText}
            </h2>
          {/if}
        </div>
        {#if theme === 'branded'}
          <p
            class="mb-4"
            class:text-0.5sm={small}
            class:md:text-base={small}
            class:lg:text-base={small}
            class:lg:text-lg={!small}
            class:lg:mb-6={!small}
          >
            {@html optionalText}
          </p>
        {/if}
        <p
          class:font-medium={!headingText || (theme === 'branded' && optionalText)}
          class:text-blue-navy={theme === 'gray'}
          class:text-white={theme === 'blue'}
          class:lg:text-lg={theme === 'branded' && !small}
          class:mb-4={!small}
          class:mb-2={small}
          class:text-0.5sm={small}
        >
          {@html instructions}
        </p>
        <form
          id="newsletterSubmit-{key}"
          class="flex flex-row justify-between"
          data-testid="{dataTestId}-form"
          onsubmit={handleSubmit}
        >
          <div class="min-h-20 grow mr-2">
            <label for="email-{key}" class="visually-hidden">{label}</label>
            <input
              id="email-{key}"
              class="w-full rounded border h-12 px-4 py-2 bg-white leading-loose placeholder-gray-bluey
              focus:outline-none focus:shadow-focus active:shadow-active"
              class:border-red={state.invalid}
              class:text-red={state.invalid}
              class:border-gray-gunmetal={state.initial || state.pending}
              class:text-gray-gunmetal={state.initial || state.pending}
              class:text-0.5sm={small}
              type="email"
              bind:this={inputNode}
              bind:value={inputValue}
              onkeydown={handleOnKeydown}
              onfocus={handleFocus}
              onblur={handleBlur}
              {placeholder}
              required
              aria-required="true"
              data-testid="{dataTestId}-input"
            />
            <span
              class="text-sm text-red leading-loose"
              class:hidden={state.initial || state.pending || state.populated}
              class:block={state.invalid}
              class:text-black={theme === 'gray'}
              class:text-white={theme === 'blue'}
              data-testid="{dataTestId}-validation-message"
            >
              {validationErrorText}
            </span>
          </div>
          <div>
            <button
              type="submit"
              class="leading-loose text-center font-medium focus:outline-none focus:shadow-focus active:shadow-active
              rounded-md whitespace-nowrap min-w-25 px-4 flex items-center justify-center h-12 hover-hover:hover:bg-blue-light active:bg-blue-malibu text-black"
              class:bg-blue-robin-egg={theme !== 'branded'}
              class:bg-blue-bright-cyan={theme === 'branded'}
              class:text-0.5sm={small}
              onclick={checkValidity}
              data-testid="{dataTestId}-button"
            >
              {#if state.pending}
                <BaseSpinner
                  className="w-6 h-6 mt-1 text-gray-opaque"
                  dataTestId="{dataTestId}-form-section-spinner"
                />
              {:else}
                <!-- prettier-ignore -->
                {submitButtonLabel}
              {/if}
            </button>
          </div>
        </form>
      {/if}
    </div>
  </div>
</div>
